import React, { useState } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { formatApiError } from '../services/api';
import { Mail, Lock, AlertCircle } from 'lucide-react';

// ERIC Logo Component
function ERICLogo({ size = 'default' }) {
  const sizes = {
    small: { text: 'text-2xl', sub: 'text-xs' },
    default: { text: 'text-4xl', sub: 'text-sm' },
    large: { text: 'text-5xl', sub: 'text-base' }
  };
  const s = sizes[size];
  
  return (
    <div className="flex flex-col">
      <div className={`font-outfit font-bold ${s.text} tracking-tight`}>
        <span className="text-emerald-400">E</span>
        <span className="text-blue-400">R</span>
        <span className="text-blue-400">I</span>
        <span className="text-orange-400">C</span>
      </div>
      <div className={`${s.sub} text-slate-300 font-medium tracking-wide`}>
        Education Risk Intelligence Center
      </div>
    </div>
  );
}

export default function LoginPage() {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const { login } = useAuth();
  const navigate = useNavigate();

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setLoading(true);

    try {
      await login(email, password);
      navigate('/dashboard');
    } catch (err) {
      setError(formatApiError(err));
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-slate-50 flex">
      {/* Left side - Image */}
      <div className="hidden lg:flex lg:w-1/2 relative">
        <img 
          src="https://images.pexels.com/photos/7406300/pexels-photo-7406300.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=650&w=940"
          alt="School building"
          className="absolute inset-0 w-full h-full object-cover"
        />
        <div className="absolute inset-0 bg-slate-900/80 flex items-center justify-center p-12">
          <div className="text-white max-w-md">
            <div className="mb-6">
              <ERICLogo size="large" />
            </div>
            <h1 className="font-outfit text-4xl font-bold mb-4">
              Building Risk Intelligence for K-12 Districts
            </h1>
            <p className="text-lg text-slate-300">
              Empower your leadership with AI-powered risk assessment, training, and monitoring tools.
            </p>
          </div>
        </div>
      </div>

      {/* Right side - Form */}
      <div className="flex-1 flex items-center justify-center p-8">
        <div className="w-full max-w-md">
          <div className="lg:hidden flex flex-col items-center mb-8">
            <div className="font-outfit font-bold text-3xl tracking-tight">
              <span className="text-emerald-500">E</span>
              <span className="text-blue-600">R</span>
              <span className="text-blue-600">I</span>
              <span className="text-orange-500">C</span>
            </div>
            <div className="text-xs text-slate-500 font-medium tracking-wide">
              Education Risk Intelligence Center
            </div>
          </div>

          <div className="bg-white border border-slate-200 rounded-xl p-8 shadow-sm">
            <h2 className="font-outfit text-2xl font-semibold text-slate-900 mb-2">Welcome back</h2>
            <p className="text-slate-500 mb-6">Sign in to your account to continue</p>

            {error && (
              <div className="bg-red-50 border border-red-200 rounded-lg p-4 mb-6 flex items-start gap-3" data-testid="login-error">
                <AlertCircle className="w-5 h-5 text-red-500 flex-shrink-0 mt-0.5" />
                <span className="text-sm text-red-700">{error}</span>
              </div>
            )}

            <form onSubmit={handleSubmit} className="space-y-4">
              <div>
                <label className="block text-xs tracking-wider uppercase font-semibold text-slate-500 mb-2">
                  Email Address
                </label>
                <div className="relative">
                  <Mail className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-slate-400" />
                  <input
                    type="email"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    className="w-full pl-10 pr-4 py-3 border border-slate-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all"
                    placeholder="admin@district.edu"
                    required
                    data-testid="login-email"
                  />
                </div>
              </div>

              <div>
                <label className="block text-xs tracking-wider uppercase font-semibold text-slate-500 mb-2">
                  Password
                </label>
                <div className="relative">
                  <Lock className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-slate-400" />
                  <input
                    type="password"
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    className="w-full pl-10 pr-4 py-3 border border-slate-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all"
                    placeholder="Enter your password"
                    required
                    data-testid="login-password"
                  />
                </div>
              </div>

              <div className="flex items-center justify-between">
                <label className="flex items-center gap-2 cursor-pointer">
                  <input type="checkbox" className="w-4 h-4 rounded border-slate-300" />
                  <span className="text-sm text-slate-500">Remember me</span>
                </label>
                <Link to="/forgot-password" className="text-sm text-blue-600 hover:underline">
                  Forgot password?
                </Link>
              </div>

              <button
                type="submit"
                disabled={loading}
                className="w-full bg-slate-900 text-white py-3 rounded-lg font-medium hover:bg-slate-800 transition-all duration-200 disabled:opacity-50 disabled:cursor-not-allowed"
                data-testid="login-submit"
              >
                {loading ? 'Signing in...' : 'Sign In'}
              </button>
            </form>

            <p className="text-center mt-6 text-slate-500">
              Don't have an account?{' '}
              <Link to="/" className="text-blue-600 hover:underline font-medium">
                Request a demo
              </Link>
            </p>
          </div>
        </div>
      </div>
    </div>
  );
}
